import {
  bleedPage,
  columns,
  contactColumn,
  FlowTemplate,
  leadIcon,
  padHorizontal,
  photoWidget,
  pill,
  sectionIcon,
  type RenderContext,
  type Zone,
  type ZoneColors,
} from "../base";
import { col, keep, padding, rect, text, vspace, wrapBox, type Node, type PageSpec } from "../ir";
import { sidebarFriendly, type RenderSection } from "../model";
import { hex, Pal, WHITE } from "../theme";

const MAIN_PAD_L = 40;
const MAIN_PAD_R = 24;
const SIDE_PAD_L = 18;
const SIDE_PAD_R = 24;

/**
 * Atlas — main story on the left, a softly tinted right column for the
 * quick-scan facts (contact, skills, languages, certifications).
 * (atlas_template.dart)
 */
export class AtlasTemplate extends FlowTemplate {
  readonly info = {
    id: "atlas",
    name: "Atlas",
    tagline: "Two columns with a soft tinted panel",
    category: "modern" as const,
    layout: "Two column",
    defaultAccent: 0xff6d28d9,
    atsFriendly: false,
    supportsPhoto: true,
  };

  readonly fonts = {
    body: "inter" as const,
    heading: "merriweather" as const,
    display: "merriweather" as const,
    headingFollowsBody: false,
    displayFollowsBody: false,
  };

  private sidebarWidth(c: RenderContext): number {
    return c.format.width * 0.32;
  }

  zoneWidth(c: RenderContext, z: Zone): number {
    const side = this.sidebarWidth(c);
    return z === "main" ? c.format.width - side - MAIN_PAD_L - MAIN_PAD_R : side - SIDE_PAD_L - SIDE_PAD_R;
  }

  private panel(c: RenderContext) {
    return Pal.tint(c.t.accent, 0.93);
  }

  colors(c: RenderContext, z: Zone): ZoneColors {
    const base = super.colors(c, z);
    if (z === "main") return base;
    return { ...base, line: Pal.tint(c.t.accent, 0.78) };
  }

  heading(c: RenderContext, s: RenderSection, z: Zone): Node {
    return this.titled(c, s.title, z, s);
  }

  private titled(c: RenderContext, title: string, z: Zone, s?: RenderSection): Node {
    const t = c.t;
    if (z === "sidebar") {
      return leadIcon(
        s ? sectionIcon(c, s, t.accentText, t.fs(9.5)) : null,
        text(title.toUpperCase(), t.style(t.body.bold, 8.4, { color: t.accentText, letterSpacing: 1.6, lineSpacing: 0 })),
        { gap: 5 }
      );
    }
    return col([
      leadIcon(s ? sectionIcon(c, s, t.accentText, t.fs(12.5)) : null, text(title, t.style(t.heading.bold, 12, { color: t.ink, lineSpacing: 0 })), {
        gap: 7,
      }),
      vspace(4),
      rect({ width: 22, height: 2.4, color: t.accent, radius: 1.2 }),
    ]);
  }

  chips(c: RenderContext, items: string[], z: Zone): Node {
    if (z === "main") return super.chips(c, items, z);
    return wrapBox(
      items.map((item) =>
        pill(item, c.t.style(c.t.body.regular, 8, { color: c.t.ink, lineSpacing: 0 }), {
          background: WHITE,
          radius: 8,
          hPad: 6.5,
          vPad: 2.8,
        })
      ),
      { spacing: 4, runSpacing: 4 }
    );
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const sideW = this.sidebarWidth(c);
    const photo = photoWidget(c, 88, { circle: false, radius: 14 });
    const sidePad = (n: Node) => padHorizontal(n, SIDE_PAD_L, SIDE_PAD_R);
    const mainPad = (n: Node) => padHorizontal(n, MAIN_PAD_L, MAIN_PAD_R);

    const sidebarSections = m.sections.filter(sidebarFriendly);
    const mainSections = m.sections.filter((s) => !sidebarFriendly(s));

    const sidebar: Node[] = [
      ...(photo ? [sidePad(padding({ bottom: t.gap(18) }, photo))] : []),
      ...(m.contacts.length
        ? [
            sidePad(
              keep([
                this.titled(c, "Contact", "sidebar"),
                vspace(this.headingGap(c, "sidebar")),
                ...contactColumn(c, {
                  style: t.style(t.body.regular, 8.1, { color: t.text, lineSpacing: 0 }),
                  iconColor: t.accent,
                  gap: 6,
                }),
                vspace(this.sectionGap(c, "sidebar")),
              ])
            ),
          ]
        : []),
      ...this.allSections(c, sidebarSections, "sidebar").map(sidePad),
    ];

    const main: Node[] = [
      mainPad(
        keep([
          m.name && text(m.name, t.style(t.display.bold, 24, { color: t.ink, letterSpacing: -0.2, lineSpacing: 0 })),
          m.headline && vspace(5),
          m.headline && text(m.headline, t.style(t.body.semiBold, 11, { color: t.accentText, lineSpacing: 0 })),
          vspace(t.gap(22)),
        ])
      ),
      ...this.allSections(c, mainSections).map(mainPad),
    ];

    return bleedPage(c, {
      top: 40,
      bottom: 30,
      bands: [{ side: "right", width: sideW, color: hex(this.panel(c)) }],
      footerRight: sideW + MAIN_PAD_R,
      footerBottom: 12,
      columns: columns({ left: main, right: sidebar, rightWidth: sideW }),
    });
  }
}
